/**
 * Calcul des séries des graphes du Dashboard à partir des vraies conversations.
 * Utilisé en repli quand le backend /analytics/dashboard ne fournit pas
 * weeklyData / hourlyActivity. Aucune dépendance externe.
 */

const WEEK_LABELS = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];

/** Lundi 00:00 de la semaine contenant `date`. */
const startOfWeek = (date) => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  const day = (d.getDay() + 6) % 7; // lundi = 0
  d.setDate(d.getDate() - day);
  return d;
};

/**
 * Horodatages d'activité d'une conversation.
 * Privilégie les messages (volume réel) ; sinon le dernier timestamp connu.
 */
const getActivityTimestamps = (conv) => {
  if (Array.isArray(conv?.messages) && conv.messages.length) {
    return conv.messages
      .map((m) => m?.createdAt || m?.timestamp || m?.sentAt)
      .filter(Boolean);
  }
  const t = conv?.lastMessageAt || conv?.updatedAt || conv?.createdAt;
  return t ? [t] : [];
};

/**
 * Activité sur 24h glissantes : 24 tranches horaires chronologiques
 * se terminant à l'heure courante. -> [{ name: '14h', value: n }, ...]
 */
export const computeHourlyActivity = (conversations = []) => {
  const counts = new Array(24).fill(0);

  const start = new Date();
  start.setMinutes(0, 0, 0);
  start.setHours(start.getHours() - 23); // 24 tranches, incluant l'heure courante

  for (const conv of conversations) {
    for (const ts of getActivityTimestamps(conv)) {
      const d = new Date(ts);
      if (Number.isNaN(d.getTime())) continue;
      const slot = Math.floor((d - start) / 3600000);
      if (slot >= 0 && slot < 24) counts[slot] += 1;
    }
  }

  return counts.map((value, i) => {
    const slot = new Date(start.getTime() + i * 3600000);
    return { name: `${String(slot.getHours()).padStart(2, '0')}h`, value };
  });
};

/**
 * Activité hebdomadaire (semaine courante Lun→Dim) :
 * conversations = conversations créées ce jour-là,
 * relances = conversations en attente (status 'pending') ce jour-là.
 * -> [{ name: 'Lun', conversations: n, relances: m }, ...]
 */
export const computeWeeklyActivity = (conversations = []) => {
  const weekStart = startOfWeek(new Date());
  const convCounts = new Array(7).fill(0);
  const relCounts = new Array(7).fill(0);

  const dayIndex = (date) => {
    const d = new Date(date);
    if (Number.isNaN(d.getTime())) return -1;
    return Math.floor((d.setHours(0, 0, 0, 0) - weekStart.getTime()) / 86400000);
  };

  for (const conv of conversations) {
    const created = conv?.createdAt || conv?.updatedAt;
    if (created) {
      const i = dayIndex(created);
      if (i >= 0 && i < 7) convCounts[i] += 1;
    }
    if (conv?.status === 'pending') {
      const t = conv?.updatedAt || conv?.createdAt;
      if (t) {
        const i = dayIndex(t);
        if (i >= 0 && i < 7) relCounts[i] += 1;
      }
    }
  }

  return WEEK_LABELS.map((name, i) => ({
    name,
    conversations: convCounts[i],
    relances: relCounts[i],
  }));
};
